// Mastery-class + channel inference. Phase C §C-Q2 + §C-Q3.
//
// Two concerns:
//   - inferChannel(skillId): reads from learningProgressions.json,
//     the single source of truth authored in Phase B §B2.
//   - inferMasteryClass(skillId): hardcoded lookup table with inline
//     rationale per the user's tracking-note directive. The
//     classification determines what counts as "true mastery" for
//     each skill (transfer required vs not, delayed review required
//     vs not, etc.).
//
// Both functions return defaults for unknown skillIds so the engine
// fails soft on a typo or a skill we haven't authored yet.

import type { MasteryChannel, MasteryClass } from "./types";
import type { LearningProgression } from "../curriculum/types/manifests";
import learningProgressions from "../curriculum/data/manifests/learningProgressions.json";

const PROGRESSIONS = learningProgressions as unknown as LearningProgression[];
const PROGRESSIONS_INDEX = new Map(PROGRESSIONS.map((p) => [p.microSkillId, p]));

/**
 * Returns the primary mastery channel for a skill, sourced from
 * learningProgressions.json. Returns "mathConceptMastery" as a
 * fail-soft default for unknown skill IDs (logged at decision time).
 */
export function inferChannel(skillId: string): MasteryChannel {
  return PROGRESSIONS_INDEX.get(skillId)?.primaryChannel ?? "mathConceptMastery";
}

/**
 * Returns the mastery class for a skill, controlling what evidence
 * counts as "true mastery". See spec §"Mastery scoring and evidence
 * windows" for the policy table that consumes this.
 *
 * Counts (per Phase C plan §C-Q3, approved with the user's adjustment):
 *   awareness:        2
 *   directPractice:  33
 *   connectedText:    4
 *   strategyTransfer: 2
 *   durableMastery:   (computed dynamically, never returned here)
 *   total:           41
 */
const CLASS_BY_SKILL: Record<string, MasteryClass> = {
  // ── awareness (2) ────────────────────────────────────────────────
  // PA-format skills where the target IS awareness, not direct
  // print decoding. Rhyming + syllable counting are auditory tasks
  // with no print contact required.
  // rationale: sounds to letters (ms.read.04; labelled oral blending until
  //   MA-1065) + oral segmenting (ms.read.05)
  //   were considered for this class but kept as directPractice because
  //   they're scaffolds INTO decoding rather than awareness ends in
  //   themselves. They probe the PA mechanism via tap-sound-boxes etc.,
  //   producing direct evidence of phonemic-blending skill the engine
  //   should count as direct-practice readiness for CVC work.
  "ms.read.0a": "awareness",  // rhyming
  "ms.read.0b": "awareness",  // syllable counting

  // ── connectedText (4) ────────────────────────────────────────────
  // Skills where TRUE mastery requires demonstrating the pattern in
  // CONNECTED TEXT, not just in isolated word lists. The spec's
  // "reading_pattern_requires_connected_text" rule lives here.
  // Note (audit fix-batch §C-Q3): the user's plan answer named
  // "decodable passages (ms.read.04, ms.read.05)" — those microSkill
  // IDs are actually Sounds to letters and Oral segmenting (PA-format,
  // not passages). The user's stated COUNTS (awareness:2,
  // connectedText:4, strategyTransfer:2, directPractice:33, total:41)
  // are unambiguous and respected. The specific 4 connectedText
  // skills chosen below are the most defensible per the spec's
  // "reading_pattern_requires_connected_text" principle.
  "ms.read.06": "connectedText",  // CVC decoding — explicitly named by user
  "ms.read.10": "connectedText",  // Sentence completion — already sentence-level
  "ms.read.11": "connectedText",  // Digraphs — pattern requires text demo
  "ms.read.13": "connectedText",  // Short passage completion — already passage-level

  // ── strategyTransfer (1) ─────────────────────────────────────────
  // Word problems: mastery requires showing the kid can move the
  // strategy across representations (equation → story → manipulative).
  // Bare-equation success is not enough.
  // rationale: math.11 add/subtract-within-20 was considered but kept
  //   as directPractice — within-20 work is still in the operations
  //   phase where direct fluency is the target. Word problems are the
  //   explicit strategy-transfer moment per the spec's MasteryClassPolicy
  //   table.
  "ms.math.15": "strategyTransfer",  // One-step word problems

  // ⭐MA-631 §3b — ms.math.12 (Missing addends within 20) moved OFF
  // strategyTransfer and explicitly onto directPractice, not deleted to the
  // default. Its 42 items are {equation: 12, directPractice: 30} with ZERO
  // narrative — "___ + 2 = 11" is not a word problem, and re-authoring it as
  // one to satisfy a gate would be backwards. Left on strategyTransfer, its
  // masteryClass would have kept `requiresWordProblemTransfer("ms.math.12")
  // === true` even after §3c's re-channel — a fresh, permanently-
  // unsatisfiable gate one field over from the one this item fixes
  // (transferGating.ts hardcodes `itemType === "wordProblem"` over a
  // 42-item pool with zero narrative). Explicit rather than deleted-to-
  // default: deleting reaches the same VALUE but reds
  // masteryClassInference.test.mjs's "8 explicit assignments" pin (T10a) —
  // an explicit entry is the honest record of a deliberate decision, not an
  // absence of one. Parity with `ms.math.11` (same pool shape — within-20
  // arithmetic — already directPractice, by the note above).
  "ms.math.12": "directPractice",  // Missing addends within 20
};

export function inferMasteryClass(skillId: string): MasteryClass {
  // Default to directPractice for any pencilwise microSkill not
  // explicitly classed above. directPractice is the safe default —
  // it requires evidence and transfer, just not the special
  // connected-text or strategy-transfer flavors.
  return CLASS_BY_SKILL[skillId] ?? "directPractice";
}

/**
 * Returns true if the skillId is one we've explicitly classified.
 * Exposed for the test suite's coverage check (ensures we don't
 * silently fall to the default for any pencilwise microSkill).
 */
export function isClassExplicitlyAssigned(skillId: string): boolean {
  return Object.prototype.hasOwnProperty.call(CLASS_BY_SKILL, skillId);
}

/** Internal — exposed for test coverage. */
export const _CLASS_BY_SKILL = CLASS_BY_SKILL;
